import { css, cva, cx } from '@repo/panda/css';
import type { ReactNode } from 'react';
import { Icon, type IconBaseProps } from '../ds/icon';
import type { MaterialSymbolName } from '../types/material-symbol-names';

const infoBannerStyles = cva({
    base: {
        display: 'flex',
        alignItems: 'center',
        gap: '12',
        paddingX: '12',
        paddingY: '12',
        borderRadius: '12',
        fontSize: 'md',
        lineHeight: '[16px]',
        fontWeight: 'medium',
        width: 'full',
        minHeight: '12',
    },
    variants: {
        variant: {
            neutral: {
                backgroundColor: 'fill.default',
                color: 'text.highlight',
                paddingX: '16',
            },
            info: {
                backgroundColor: 'yellow.500/15',
                color: 'yellow.200',
            },
            success: {
                backgroundColor: 'green.800/30',
                color: 'limeGreen.900',
            },
            gamify: {
                backgroundColor: 'green.800/30',
                color: 'green.400',
            },
            error: {
                backgroundColor: 'red.500/30',
                color: 'red.400',
            },
            warning: {
                backgroundColor: 'orange.800/30',
                color: 'orange.400',
            },
        },
        interactive: {
            true: {
                _hover: {
                    filter: '[brightness(1.1)]',
                    cursor: 'pointer',
                },
            },
            false: {},
        },
        borderRadius: {
            full: { borderRadius: 'full' },
            md: { borderRadius: '12' },
            sm: { borderRadius: '8' },
            xs: { borderRadius: '4' },
            none: { borderRadius: '[0px]' },
        },
    },
    defaultVariants: {
        variant: 'info',
        interactive: false,
        borderRadius: 'md',
    },
});

const defaultIcons: Record<NonNullable<InfoBannerProps['variant']>, MaterialSymbolName> = {
    neutral: 'info',
    info: 'info',
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
    gamify: 'gamepad',
};

const iconColorStyles: Record<NonNullable<InfoBannerProps['variant']>, string> = {
    neutral: css({ color: 'text.secondary' }),
    info: css({ color: 'yellow.400' }),
    success: css({ color: 'green.400' }),
    error: css({ color: 'red.400' }),
    warning: css({ color: 'orange.400' }),
    gamify: css({ color: 'green.400' }),
};

export interface InfoBannerProps {
    variant?: 'neutral' | 'info' | 'success' | 'error' | 'warning' | 'gamify';
    icon?: MaterialSymbolName;
    iconProps?: Omit<IconBaseProps, 'icon'>;
    children: ReactNode;
    className?: string;
    interactive?: boolean;
    borderRadius?: 'full' | 'md' | 'sm' | 'xs' | 'none';
    /** The one thing the player can do about the banner, at its end. */
    action?: ReactNode;
}

export const InfoBanner = ({
    variant = 'info',
    icon,
    iconProps,
    children,
    className,
    interactive = false,
    borderRadius = 'md',
    action,
}: InfoBannerProps) => {
    return (
        <div className={cx(infoBannerStyles({ variant, interactive, borderRadius }), className)}>
            <Icon
                fill
                icon={icon ?? defaultIcons[variant]}
                size="md"
                {...iconProps}
                className={cx(css({ flexShrink: 0 }), iconColorStyles[variant], iconProps?.className)}
                decorative
            />
            {children}
            {action && <div className={css({ marginLeft: 'auto', flexShrink: 0 })}>{action}</div>}
        </div>
    );
};
